










import {Component, Prop, Vue} from 'vue-property-decorator'
import {EnvHelper} from '@/helpers/EnvHelper'

/* eslint-disable no-unused-vars */
/** Minimal surface of the global `grecaptcha` object we depend on. */
interface Grecaptcha {
  render(
    container: HTMLElement,
    options: {
      sitekey: string
      callback: (token: string) => void
      'expired-callback': () => void
      'error-callback': () => void
    }
  ): number
  reset(widgetId: number): void
}
/* eslint-enable no-unused-vars */

/** reCAPTCHA hangs both the API object and our load callback off `window`. */
type RecaptchaWindow = Window & {
  grecaptcha?: Grecaptcha
  onNdappRecaptchaLoad?: () => void
}

const recaptchaWindow = window as RecaptchaWindow

const ONLOAD_CALLBACK = 'onNdappRecaptchaLoad'
const SCRIPT_ID = 'ndapp-recaptcha'

/**
 * A blocked script can leave `onerror` unfired and the callback never called,
 * which would hang the promise and render neither a widget nor an error. Failing
 * loudly after this long keeps that from looking like an empty field.
 */
const LOAD_TIMEOUT_MS = 10000

/**
 * Loads the reCAPTCHA script once per page and resolves when `grecaptcha` is
 * ready. Kept at module scope so several widgets — or a revisited route — reuse
 * the same load instead of injecting the script again.
 */
let loader: Promise<Grecaptcha> | null = null

function loadRecaptcha(): Promise<Grecaptcha> {
  if (loader) {
    return loader
  }

  loader = new Promise<Grecaptcha>((resolve, reject) => {
    if (recaptchaWindow.grecaptcha) {
      resolve(recaptchaWindow.grecaptcha)
      return
    }

    const timeout = setTimeout(
      () =>
        reject(
          new Error(
            `reCAPTCHA did not load within ${LOAD_TIMEOUT_MS}ms — it is most ` +
              'likely blocked by an extension, a proxy or a CSP'
          )
        ),
      LOAD_TIMEOUT_MS
    )

    recaptchaWindow.onNdappRecaptchaLoad = () => {
      clearTimeout(timeout)

      if (recaptchaWindow.grecaptcha) {
        resolve(recaptchaWindow.grecaptcha)
      } else {
        reject(new Error('grecaptcha missing after load'))
      }
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = `https://www.google.com/recaptcha/api.js?onload=${ONLOAD_CALLBACK}&render=explicit`
    script.async = true
    script.defer = true
    script.onerror = () => {
      clearTimeout(timeout)
      reject(new Error('could not load reCAPTCHA'))
    }

    document.head.appendChild(script)
  }).catch(error => {
    // Surfacing this beats a silently empty field when someone reports the bug.
    console.error('[ContactCaptcha]', error)

    // Let a later mount retry instead of caching the rejection forever.
    loader = null
    throw error
  })

  return loader
}

@Component
export default class ContactCaptcha extends Vue {
  /** The solved token, or null while unsolved/expired. Used with v-model. */
  @Prop({type: String, default: null}) value!: string | null

  hasFailed = false

  private widgetId: number | null = null
  private grecaptcha: Grecaptcha | null = null

  get siteKey() {
    return EnvHelper.VUE_APP_RECAPTCHA_SITE_KEY
  }

  async mounted() {
    if (!this.siteKey) {
      // Misconfigured environment: fail loudly rather than rendering nothing.
      this.hasFailed = true
      this.$emit('unavailable')
      return
    }

    try {
      this.grecaptcha = await loadRecaptcha()
    } catch {
      this.hasFailed = true
      this.$emit('unavailable')
      return
    }

    // The component may have been torn down while the script was loading.
    const container = this.$refs.widget as HTMLElement | undefined
    if (container) {
      this.renderWidget(container)
    }
  }

  /**
   * Must not be called `render`: vue-class-component treats that name as the
   * component's own render function instead of a method, which silently
   * replaces the template with its return value.
   */
  renderWidget(container: HTMLElement) {
    if (!this.grecaptcha) {
      return
    }

    this.widgetId = this.grecaptcha.render(container, {
      sitekey: this.siteKey,
      callback: (token: string) => this.$emit('input', token),
      // A solved token only lives two minutes; drop it so the form re-blocks.
      'expired-callback': () => this.$emit('input', null),
      'error-callback': () => this.$emit('input', null),
    })
  }

  /** Clears the checkbox so a new token can be collected. */
  reset() {
    if (this.grecaptcha && this.widgetId !== null) {
      this.grecaptcha.reset(this.widgetId)
    }

    this.$emit('input', null)
  }
}
